import { personRef } from "@/content/author";
import { BOOK_TITLES } from "@/content/books";

const BOOK_TITLE_SET = new Set(Object.values(BOOK_TITLES).map((b) => b.titleTr.trim().toLocaleLowerCase("tr-TR")));
/** HTML title only: a talk sharing a book's title is marked as a talk so the two pages stay distinct. */
function talkDocTitle(topic: string) {
  return BOOK_TITLE_SET.has(topic.trim().toLocaleLowerCase("tr-TR")) ? `${topic} — Konuşma — Arda Öztaşkın` : `${topic} — Arda Öztaşkın`;
}
import { createFileRoute, notFound } from "@tanstack/react-router";
import { talks, formatTalkDate } from "@/lib/talks";
import { getTranscript, type TranscriptSegment } from "@/lib/transcripts.functions";
import { YouTubeFacade } from "@/components/YouTubeFacade";
import { hreflangLinks } from "@/lib/seo";
import { useT } from "@/lib/i18n";
import { PageIntro, PageSection } from "@/components/site/page";

function findTalk(id: string) {
  return talks.find((t) => t.id === id) ?? null;
}

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export const Route = createFileRoute("/konusmalar_/$id")({
  loader: async ({ params }) => {
    const talk = findTalk(params.id);
    if (!talk) throw notFound();
    const transcript = await getTranscript({ data: { videoId: talk.id, lang: "tr" } }).catch(
      () => null,
    );
    return { talk, transcript };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Talk — Arda Öztaşkın" }] };
    const { talk, transcript } = loaderData;
    const desc = `${talk.topic} — ${talk.program}. Arda Öztaşkın'ın ${formatTalkDate(talk.date, "tr")} tarihli konuşması.`;
    const url = `https://akillisurdurulebilirlik.com/konusmalar/${talk.id}`;
    return {
      meta: [
        { title: talkDocTitle(talk.topic) },
        { name: "description", content: desc },
        { property: "og:title", content: talk.topic },
        { property: "og:description", content: desc },
        { property: "og:type", content: "video.other" },
        { property: "og:url", content: url },
        { property: "og:image", content: `https://i.ytimg.com/vi/${talk.id}/maxresdefault.jpg` },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/konusmalar/${talk.id}`)],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: talk.topic,
            description: desc,
            thumbnailUrl: [
              `https://i.ytimg.com/vi/${talk.id}/maxresdefault.jpg`,
              `https://i.ytimg.com/vi/${talk.id}/hqdefault.jpg`,
            ],
            uploadDate: talk.date,
            contentUrl: talk.url,
            embedUrl: `https://www.youtube.com/embed/${talk.id}`,
            inLanguage: "tr",
            publisher: { "@type": "Organization", name: talk.program },
            author: personRef(),
            ...(transcript && transcript.status === "ok" && transcript.plainText
              ? { transcript: transcript.plainText.slice(0, 5000) }
              : {}),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Ana Sayfa",
                item: "https://akillisurdurulebilirlik.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Konuşmalar",
                item: "https://akillisurdurulebilirlik.com/konusmalar",
              },
              { "@type": "ListItem", position: 3, name: talk.topic, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: TalkDetail,
});

function TalkDetail() {
  const { talk, transcript } = Route.useLoaderData();
  const { lang } = useT();
  const tr = lang === "tr";

  return (
    <>
      <PageIntro
        crumbs={[{ label: "Konuşmalar", to: "/konusmalar" }, { label: talk.topic }]}
        kicker={formatTalkDate(talk.date, lang)}
        title={talk.topic}
        lede={talk.program}
      />

      <PageSection>
        <YouTubeFacade videoId={talk.id} title={talk.topic} watchUrl={talk.url} className="mb-8" />

        <a
          href={talk.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center gap-2 rounded-full bg-ink px-5 text-[11px] font-bold uppercase tracking-[0.2em] text-paper transition-colors hover:bg-clay"
        >
          {tr ? "YouTube'da Aç" : "Open on YouTube"} <span aria-hidden>↗</span>
        </a>
      </PageSection>

      <PageSection title={tr ? "Transkript" : "Transcript"}>
        {!transcript || transcript.status !== "ok" || transcript.segments.length === 0 ? (
          <p className="text-sm text-ink/85">
            {tr
              ? "Bu video için otomatik altyazı bulunamadı."
              : "Auto-captions are not available for this video."}
          </p>
        ) : (
          <>
            <p className="mb-4 text-[11px] uppercase tracking-[0.2em] text-ink/70">
              {transcript.isAuto
                ? tr
                  ? "Otomatik altyazıdan oluşturuldu — okuma hataları içerebilir."
                  : "Generated from auto-captions — may contain reading errors."
                : tr
                  ? "Resmi altyazıdan oluşturuldu."
                  : "From official captions."}
            </p>
            <ol className="space-y-2 text-[15px] leading-relaxed text-ink/85">
              {transcript.segments.map((seg: TranscriptSegment, i: number) => (
                <li key={i} className="flex gap-3">
                  <a
                    href={`${talk.url}&t=${seg.t}s`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 shrink-0 pt-1 text-[11px] uppercase tracking-[0.15em] text-theme-tech tabular-nums hover:underline"
                  >
                    {formatTime(seg.t)}
                  </a>
                  <span>{seg.text}</span>
                </li>
              ))}
            </ol>
          </>
        )}
      </PageSection>
    </>
  );
}
